import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useOutletContext, useNavigate, Link } from "react-router";
import { TimePeriod } from "../features/energy/types";
import { Project, ProjectWithGenerationSummary, Site, Document, User } from "../types";
import OperationalData from "../features/energy/components/OperationalData";
import { getHealthStatusDot } from "../features/energy/utils";
import Documents from "./Documents";
import { DashboardOutletContext } from "./Dashboard";
import { siteToMarker } from "../utils";
import { ArrowLeftCircleIcon } from "@heroicons/react/20/solid";
import { EnvelopeIcon, PlusCircleIcon, UsersIcon, ChevronDownIcon } from "@heroicons/react/16/solid";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import { getProjectDocuments } from "../api";
import StatusFilterDropdown from "./StatusFilterDropdown";

interface ProjectPageLoaderData {
    projectWithGenerationSummary: ProjectWithGenerationSummary;
}

const ProjectHeader = ({ project }: { project: Project }) => (
    <header className="pb-6">
        <div>
            <h1 className="text-3xl font-semibold text-pacificblue-900">{project.name}</h1>
            {project.country
                ? <h3 className="text-lg font-bold text-pacificblue-700">{project.locality ? `${project.locality}, ` : ''}{project.country}</h3>
                : <></>
            }
            <Link to="/dashboard" className="mt-1 text-sm inline-flex items-center text-gray-500 hover:underline"><ArrowLeftCircleIcon className='w-4 h-4 me-1' />Dashboard </Link>
        </div>
    </header>
);

const Description = () => (
    <div className="row-span-1 col-span-1">
        <p className="text-xs text-gray-700">
            Brief description of the project.
        </p>
    </div>
);

const Contacts = () => (
    <div className="mt-4">
        <h3 className="font-semibold mb-2">Contacts</h3>
        <div className="space-y-2">
            {[
                { name: "Person 1", role: "Project Manager", email: "person-1@example.com", phone: "+1 555-1234" },
                { name: "Person 2", role: "Technical Lead", email: "person-2@example.com", phone: "+1 555-5678" },
                { name: "Person 3", role: "Community Liaison", email: "person-3@example.com", phone: "+1 555-9012" }
            ].map((contact, index) => (
                <div key={index} className="flex items-start p-2 bg-stone-100 rounded">
                    <div className="w-8 h-8 mr-2 bg-stone-200 rounded-full flex items-center justify-center">
                        {contact.name.charAt(0)}
                    </div>
                    <div className="text-xs">
                        <p className="font-semibold">{contact.name}</p>
                        <p className="text-gray-600">{contact.role}</p>
                        <p className="text-gray-500">{contact.email}</p>
                        <p className="text-gray-500">{contact.phone}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const ProjectInfo = ({ project }: { project: Project }) => (
    <></>
    // <div className="row-span-3 col-span-1 flex flex-col gap-4 pb-4">
    //     <Description />
    // </div>
);

const InstallationDetails = () => (
    <div className="my-4">
        <h2 className="text-xl font-bold mb-2 text-dark">Installation Details</h2>
        <div className="grid grid-cols-2 gap-4">
            {
                ['Solar PV', 'BESS'].map((name) => (
                    <div key={name} className="bg-white rounded-lg shadow p-4">
                        <h3 className="font-semibold">{name}</h3>
                        <ul className="text-sm text-gray-600">
                            <li>Installed Modules</li>
                            <li>Model</li>
                            <li>Install Date</li>
                            <li>Subcontractor</li>
                        </ul>
                    </div>
                ))
            }
        </div>
    </div>
);

const ProjectPage = () => {
    const { projectWithGenerationSummary } = useLoaderData() as ProjectPageLoaderData;
    const { user, setHoveredSiteId, setMarkers, statusFilter, setStatusFilter } = useOutletContext<DashboardOutletContext>();
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<Document[]>();
    const [tableView, setTableView] = useState<'details' | 'installer'>('details');
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const [sitesExpanded, setSitesExpanded] = useState(false);
    const sitesScrollRef = useRef<HTMLDivElement>(null);

    const checkScroll = () => {
        const el = sitesScrollRef.current;
        if (el) {
            setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
        }
    };

    useEffect(() => {
        handleDocumentsChange();
    }, []);

    const handleDocumentsChange = async () => {
        try {
            const updatedDocuments = await getProjectDocuments(projectWithGenerationSummary.instance.id);
            setDocuments(updatedDocuments);
        } catch (error) {
            console.error('Failed to refresh documents:', error);
        }
    };

    const handleSiteClick = (siteId: number) => {
        navigate(`/dashboard/site/${siteId}`);
    };

    // Filter sites for map/table display (considers coordinates)
    const filterSitesForDisplay = (sites: Site[] | null | undefined) => {
        return sites?.filter(site => {
            const hasCoordinates = site.latitude && site.longitude;
            if (!hasCoordinates) {
                return statusFilter['not-on-map'];
            }
            const status = site.health_status as keyof typeof statusFilter;
            if (status in statusFilter) {
                return statusFilter[status];
            }
            return true;
        });
    };

    // Filter sites for operational data (only by health status, not coordinates)
    const filterSitesForData = (sites: Site[] | null | undefined) => {
        return sites?.filter(site => {
            const status = site.health_status as keyof typeof statusFilter;
            if (status in statusFilter) {
                return statusFilter[status];
            }
            return true;
        });
    };

    // Memoize filtered sites for display
    const filteredSites = useMemo(() =>
        filterSitesForDisplay(projectWithGenerationSummary.instance.sites),
        [statusFilter, projectWithGenerationSummary.instance.sites]
    );

    // Memoize filtered site IDs for operational data (by health status only)
    // Always return an array (possibly empty), never undefined
    const filteredSiteIds = useMemo(() =>
        filterSitesForData(projectWithGenerationSummary.instance.sites)?.map(s => s.id) ?? [],
        [statusFilter, projectWithGenerationSummary.instance.sites]
    );

    useEffect(() => {
        checkScroll();
    }, [filteredSites, sitesExpanded]);

    useEffect(() => {
        setMarkers(filteredSites?.map(site => siteToMarker(site)) ?? []);
    }, [filteredSites, setMarkers]);

    if (!projectWithGenerationSummary) {
        return <div>Project not found</div>;
    }

    return (
        <div className="px-4">
            <ProjectHeader project={projectWithGenerationSummary.instance} />
            <ProjectInfo project={projectWithGenerationSummary.instance} />
            <div className="grid grid-cols-1 gap-6">
                <div>
                    <div className="flex justify-between items-end mb-2">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-pacificblue-950">Sites</h2>
                            <StatusFilterDropdown
                                statusFilter={statusFilter}
                                onStatusFilterChange={setStatusFilter}
                                disabled={isDataLoading}
                            />
                        </div>
                        <div className="flex gap-2 items-center">
                            <button
                                className={`btn btn-xs btn-ghost gap-1 w-auto ${tableView === 'installer' ? 'btn-active' : ''}`}
                                onClick={() => setTableView(tableView === 'details' ? 'installer' : 'details')}
                                title={tableView === 'details' ? 'Show installer information' : 'Show site details'}
                            >
                                <IdentificationIcon className="w-4 h-4" />
                                {tableView === 'details' ? 'Show' : 'Hide'} Installer
                            </button>
                            {user?.is_superuser &&
                                <Link
                                    to={`/dashboard/project/${projectWithGenerationSummary.instance.id}/invite`}
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                    Invite
                                    <EnvelopeIcon className="w-4 h-4 inline ml-1" />
                                </Link>
                            }
                            <Link
                                to={`/dashboard/project/${projectWithGenerationSummary.instance.id}/installers`}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Installers
                                <UsersIcon className="w-4 h-4 inline ml-1" />
                            </Link>
                            <Link
                                to={`/dashboard/project/${projectWithGenerationSummary.instance.id}/register`}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Register
                                <PlusCircleIcon className="w-4 h-4 inline ml-1" />
                            </Link>
                        </div>
                    </div>
                    <div className="relative">
                        <div ref={sitesScrollRef} onScroll={checkScroll} className={`${sitesExpanded ? 'h-[80vh]' : 'h-60'} overflow-y-auto overflow-x-auto transition-all duration-300 pb-6`}>
                            <table className="table-auto w-full text-left">
                                <thead>
                                    <tr className="text-pacificblue-900">
                                        <th className="ps-3 pe-1 pb-2">Name</th>
                                        {tableView === 'details' ? (
                                            <>
                                                <th className="pe-1 pb-2">Address</th>
                                                <th className="pe-1 pb-2">Solar</th>
                                                <th className="pe-1 pb-2">Health</th>
                                                <th className="pe-1 pb-2">BESS</th>
                                                <th className="pe-1 pb-2">Health</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="pe-1 pb-2">Installer Name</th>
                                                <th className="pe-1 pb-2">Installer Email</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSites?.length
                                        ? filteredSites?.map((v: Site) => (
                                            <tr
                                                key={v.id}
                                                className="rounded-full hover:bg-pacificblue-600/25 cursor-pointer"
                                                onClick={() => handleSiteClick(v.id)}
                                                onMouseEnter={() => setHoveredSiteId(v.id)}
                                                onMouseLeave={() => setHoveredSiteId(null)}
                                            >
                                                <td className="ps-3 pe-1 py-1 rounded-l-full whitespace-nowrap truncate max-w-[200px]">{v.name}</td>
                                                {tableView === 'details' ? (
                                                    <>
                                                        <td className="pe-1 py-1 whitespace-nowrap truncate max-w-[200px]">{v.address}</td>
                                                        {/* FIXME: add sites-level data */}
                                                        <td className="pe-1 py-1 whitespace-nowrap">{(v.generation_kw || '-') + ' kW'}</td>
                                                        <td className="pe-1 py-1 text-center whitespace-nowrap">{getHealthStatusDot(v.health_status)}</td>
                                                        <td className="pe-1 py-1 whitespace-nowrap">{(v.storage_capacity_kwh || '-') + ' kWh'}</td>
                                                        <td className="pe-1 py-1 text-center rounded-r-full whitespace-nowrap">{getHealthStatusDot(v.health_status)}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="pe-1 py-1 whitespace-nowrap truncate max-w-[200px]">{v.installer?.name || '-'}</td>
                                                        <td className="pe-1 py-1 rounded-r-full whitespace-nowrap truncate max-w-[200px]">{v.installer?.email || '-'}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                        : <tr><td colSpan={tableView === 'details' ? 6 : 3} className="p-3 text-gray-500">
                                            {projectWithGenerationSummary.instance.sites?.length === 0
                                                ? 'No sites registered.'
                                                : 'No sites match the current filter.'}
                                        </td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                            <button
                                onClick={() => setSitesExpanded(!sitesExpanded)}
                                className={`${canScrollDown ? 'bg-gradient-to-t from-base-100 to-transparent pt-4' : ''} w-full flex justify-center pb-1 cursor-pointer hover:opacity-80`}
                            >
                                <ChevronDownIcon className={`w-5 h-5 text-pacificblue-700 transition-transform duration-300 ${sitesExpanded ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>
                <div>
                    <OperationalData instanceWithGenerationSummary={projectWithGenerationSummary} initPeriod={TimePeriod.Month} filteredSiteIds={filteredSiteIds} onLoadingChange={setIsDataLoading} />
                </div>
                <Documents project={{ ...projectWithGenerationSummary.instance, documents: documents ?? [] }} onDocumentsChange={handleDocumentsChange} />
            </div>
        </div>
    );
};

export default ProjectPage;
